// Wave 20.50 — GPX for a planned route.
//
// A planned loop that can't leave the screen isn't much use. GPX is the
// one format every head unit and route app takes: Garmin Connect, Wahoo,
// Komoot, Ride with GPS, Apple Watch via a companion app. No vendor
// OAuth, no API keys, no app review — the rider's own file, handed to
// whatever they already use.
//
// A <trk> (not <rte>) because Garmin Connect and Wahoo both import a
// track as a course directly, while <rte> support is patchier.
//
// Mirrored on mobile in mobile/src/services/gpx.ts — same output, the
// only difference is how the bytes reach the rider (download vs the iOS
// share sheet). Keep them identical.

export type Coord = [number, number]; // [lng, lat]

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export interface GpxOptions {
  name: string;
  coords: Coord[];
  /** When the ride is planned for — written as <metadata><time>. */
  timeIso?: string;
  description?: string;
}

export function buildGpx({ name, coords, timeIso, description }: GpxOptions): string {
  const when = timeIso ?? new Date().toISOString();
  const pts = coords
    .filter((c) => Array.isArray(c) && c.length >= 2 && isFinite(c[0]) && isFinite(c[1]))
    .map((c) => `      <trkpt lat="${c[1].toFixed(6)}" lon="${c[0].toFixed(6)}" />`)
    .join('\n');
  return `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="aero.chat"
     xmlns="http://www.topografix.com/GPX/1/1"
     xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
     xsi:schemaLocation="http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd">
  <metadata>
    <name>${esc(name)}</name>
${description ? `    <desc>${esc(description)}</desc>\n` : ''}    <time>${esc(when)}</time>
  </metadata>
  <trk>
    <name>${esc(name)}</name>
    <trkseg>
${pts}
    </trkseg>
  </trk>
</gpx>
`;
}

/** Filesystem-safe filename for a route. */
export function gpxFilename(name: string): string {
  const slug = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
  return `${slug || 'aero-chat-route'}.gpx`;
}

/** Hand the file to the browser. Matches the house download helper in
 *  services/shareCard.ts — deferred revoke so Safari doesn't cancel the
 *  save out from under itself. */
export function downloadGpx(name: string, gpx: string): void {
  const blob = new Blob([gpx], { type: 'application/gpx+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = gpxFilename(name);
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 2000);
}
